import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';

const PaymentRemindersDunning = () => {
  const [activeTab, setActiveTab] = useState('reminders');
  const [schedules, setSchedules] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showStepModal, setShowStepModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [editingStep, setEditingStep] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [reminderFilter, setReminderFilter] = useState('all');

  const [scheduleForm, setScheduleForm] = useState({ name: '', description: '', is_default: false });
  const [stepForm, setStepForm] = useState({
    step_number: 1, days_after_due: 1, action_type: 'email',
    subject: '', message_template: '', is_active: true
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [schedulesRes, remindersRes] = await Promise.all([
        supabase?.from('dunning_schedules')?.select('*, dunning_steps(*)')?.order('created_at'),
        supabase?.from('payment_reminders')?.select('*, invoice:invoices(invoice_number, total_amount, due_date), customer:customers(name, email)')?.order('scheduled_at', { ascending: false })?.limit(100)
      ]);
      setSchedules(schedulesRes?.data || []);
      setReminders(remindersRes?.data || []);
    } catch (err) {
      console.error('Error fetching dunning data:', err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const handleSaveSchedule = async () => {
    try {
      if (selectedSchedule?.id) {
        await supabase?.from('dunning_schedules')?.update({ name: scheduleForm?.name, description: scheduleForm?.description, is_default: scheduleForm?.is_default, updated_at: new Date()?.toISOString() })?.eq('id', selectedSchedule?.id);
      } else {
        await supabase?.from('dunning_schedules')?.insert([{ name: scheduleForm?.name, description: scheduleForm?.description, is_default: scheduleForm?.is_default }]);
      }
      setShowScheduleModal(false);
      setSelectedSchedule(null);
      setScheduleForm({ name: '', description: '', is_default: false });
      showSuccess('Schedule saved successfully');
      fetchData();
    } catch (err) {
      console.error('Error saving schedule:', err?.message);
    }
  };

  const handleSaveStep = async () => {
    try {
      if (editingStep?.id) {
        await supabase?.from('dunning_steps')?.update({ ...stepForm, updated_at: new Date()?.toISOString() })?.eq('id', editingStep?.id);
      } else {
        await supabase?.from('dunning_steps')?.insert([{ ...stepForm, schedule_id: selectedSchedule?.id }]);
      }
      setShowStepModal(false);
      setEditingStep(null);
      setStepForm({ step_number: 1, days_after_due: 1, action_type: 'email', subject: '', message_template: '', is_active: true });
      showSuccess('Step saved successfully');
      fetchData();
    } catch (err) {
      console.error('Error saving step:', err?.message);
    }
  };

  const handleDeleteStep = async (stepId) => {
    if (!window.confirm('Delete this reminder step?')) return;
    await supabase?.from('dunning_steps')?.delete()?.eq('id', stepId);
    showSuccess('Step deleted');
    fetchData();
  };

  const handleToggleSchedule = async (schedule) => {
    await supabase?.from('dunning_schedules')?.update({ is_active: !schedule?.is_active, updated_at: new Date()?.toISOString() })?.eq('id', schedule?.id);
    showSuccess(`Schedule ${!schedule?.is_active ? 'activated' : 'deactivated'}`);
    fetchData();
  };

  const handleSendReminder = async (reminder) => {
    try {
      await supabase?.from('payment_reminders')?.update({ status: 'sent', sent_at: new Date()?.toISOString(), updated_at: new Date()?.toISOString() })?.eq('id', reminder?.id);
      showSuccess('Reminder marked as sent');
      fetchData();
    } catch (err) {
      console.error('Error sending reminder:', err?.message);
    }
  };

  const handleScheduleReminders = async () => {
    try {
      const overdueRes = await supabase?.from('invoices')?.select('*, customer:customers(name, email)')?.eq('invoice_status', 'overdue');
      const overdueInvoices = overdueRes?.data || [];
      const defaultSchedule = schedules?.find(s => s?.is_default && s?.is_active);
      if (!defaultSchedule || overdueInvoices?.length === 0) {
        showSuccess('No overdue invoices or no active default schedule found');
        return;
      }
      const steps = defaultSchedule?.dunning_steps || [];
      const remindersToInsert = [];
      overdueInvoices?.forEach(invoice => {
        const dueDate = new Date(invoice?.due_date);
        steps?.forEach(step => {
          const scheduledAt = new Date(dueDate);
          scheduledAt?.setDate(scheduledAt?.getDate() + step?.days_after_due);
          remindersToInsert?.push({
            invoice_id: invoice?.id,
            customer_id: invoice?.customer_id,
            dunning_step_id: step?.id,
            schedule_id: defaultSchedule?.id,
            reminder_type: 'payment_reminder',
            status: 'pending',
            scheduled_at: scheduledAt?.toISOString(),
            recipient_email: invoice?.customer?.email,
            subject: step?.subject?.replace('{{invoice_number}}', invoice?.invoice_number),
            message: step?.message_template?.replace('{{customer_name}}', invoice?.customer?.name)?.replace('{{invoice_number}}', invoice?.invoice_number)?.replace('{{amount}}', `$${parseFloat(invoice?.total_amount || 0)?.toFixed(2)}`)
          });
        });
      });
      if (remindersToInsert?.length > 0) {
        await supabase?.from('payment_reminders')?.insert(remindersToInsert);
        showSuccess(`Scheduled ${remindersToInsert?.length} reminders for ${overdueInvoices?.length} overdue invoices`);
        fetchData();
      }
    } catch (err) {
      console.error('Error scheduling reminders:', err?.message);
    }
  };

  const filteredReminders = reminders?.filter(r => reminderFilter === 'all' || r?.status === reminderFilter);

  const getStatusBadge = (status) => {
    const map = {
      pending: 'bg-yellow-100 text-yellow-800',
      sent: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-600'
    };
    return map?.[status] || 'bg-gray-100 text-gray-600';
  };

  const formatDate = (d) => d ? new Date(d)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';
  const formatCurrency = (v) => `$${parseFloat(v || 0)?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

  const stats = {
    total: reminders?.length,
    pending: reminders?.filter(r => r?.status === 'pending')?.length,
    sent: reminders?.filter(r => r?.status === 'sent')?.length,
    failed: reminders?.filter(r => r?.status === 'failed')?.length
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet><title>Payment Reminders & Dunning | FieldServi</title></Helmet>
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Payment Reminders & Dunning</h1>
            <p className="text-muted-foreground mt-1">Automate overdue invoice follow-ups with configurable reminder schedules</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleScheduleReminders}>
              <Icon name="CalendarClock" size={16} className="mr-2" />Schedule Reminders
            </Button>
            <Button onClick={() => { setSelectedSchedule(null); setScheduleForm({ name: '', description: '', is_default: false }); setShowScheduleModal(true); }}>
              <Icon name="Plus" size={16} className="mr-2" />New Schedule
            </Button>
          </div>
        </div>

        {successMsg && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
            <Icon name="CheckCircle" size={16} />{successMsg}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Reminders', value: stats?.total, icon: 'Bell', color: 'text-blue-600 bg-blue-50' },
            { label: 'Pending', value: stats?.pending, icon: 'Clock', color: 'text-yellow-600 bg-yellow-50' },
            { label: 'Sent', value: stats?.sent, icon: 'CheckCircle', color: 'text-green-600 bg-green-50' },
            { label: 'Failed', value: stats?.failed, icon: 'XCircle', color: 'text-red-600 bg-red-50' }
          ]?.map(stat => (
            <div key={stat?.label} className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${stat?.color}`}><Icon name={stat?.icon} size={18} /></div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stat?.value}</p>
                  <p className="text-xs text-muted-foreground">{stat?.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border mb-6">
          {[{ id: 'reminders', label: 'Reminder Queue', icon: 'Bell' }, { id: 'schedules', label: 'Dunning Schedules', icon: 'Settings' }]?.map(tab => (
            <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab?.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}>
              <Icon name={tab?.icon} size={16} />{tab?.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
        ) : activeTab === 'reminders' ? (
          <div>
            {/* Filter */}
            <div className="flex gap-2 mb-4 flex-wrap">
              {['all', 'pending', 'sent', 'failed', 'cancelled']?.map(f => (
                <button key={f} onClick={() => setReminderFilter(f)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${
                    reminderFilter === f ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}>{f === 'all' ? `All (${reminders?.length})` : `${f} (${reminders?.filter(r => r?.status === f)?.length})`}
                </button>
              ))}
            </div>
            {filteredReminders?.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Icon name="Bell" size={48} className="mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">No reminders found</p>
                <p className="text-sm mt-1">Click "Schedule Reminders" to generate reminders for overdue invoices</p>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      {['Invoice', 'Customer', 'Type', 'Scheduled', 'Status', 'Actions']?.map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredReminders?.map(reminder => (
                      <tr key={reminder?.id} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3">
                          <div className="font-medium text-foreground">{reminder?.invoice?.invoice_number || 'N/A'}</div>
                          <div className="text-xs text-muted-foreground">{formatCurrency(reminder?.invoice?.total_amount)}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-foreground">{reminder?.customer?.name || 'N/A'}</div>
                          <div className="text-xs text-muted-foreground">{reminder?.recipient_email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="capitalize text-foreground">{reminder?.reminder_type?.replace('_', ' ')}</span>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{formatDate(reminder?.scheduled_at)}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadge(reminder?.status)}`}>
                            {reminder?.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {reminder?.status === 'pending' && (
                            <Button size="sm" variant="outline" onClick={() => handleSendReminder(reminder)}>
                              <Icon name="Send" size={12} className="mr-1" />Send
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {schedules?.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Icon name="Settings" size={48} className="mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">No dunning schedules</p>
                <p className="text-sm mt-1">Create a schedule to automate payment reminders</p>
              </div>
            ) : schedules?.map(schedule => (
              <div key={schedule?.id} className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${schedule?.is_active ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                    <div>
                      <h3 className="font-semibold text-foreground">{schedule?.name}</h3>
                      <p className="text-sm text-muted-foreground">{schedule?.description}</p>
                    </div>
                    {schedule?.is_default && <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full font-medium">Default</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => {
                      setSelectedSchedule(schedule);
                      setStepForm({ step_number: (schedule?.dunning_steps?.length || 0) + 1, days_after_due: 1, action_type: 'email', subject: '', message_template: '', is_active: true });
                      setShowStepModal(true);
                    }}>
                      <Icon name="Plus" size={12} className="mr-1" />Add Step
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => {
                      setSelectedSchedule(schedule);
                      setScheduleForm({ name: schedule?.name, description: schedule?.description || '', is_default: schedule?.is_default });
                      setShowScheduleModal(true);
                    }}>
                      <Icon name="Edit" size={12} />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleToggleSchedule(schedule)}>
                      {schedule?.is_active ? <Icon name="Pause" size={12} /> : <Icon name="Play" size={12} />}
                    </Button>
                  </div>
                </div>
                <div className="p-4">
                  {schedule?.dunning_steps?.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No steps configured. Add steps to define the reminder sequence.</p>
                  ) : (
                    <div className="space-y-2">
                      {schedule?.dunning_steps?.sort((a, b) => a?.step_number - b?.step_number)?.map((step, idx) => (
                        <div key={step?.id} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">{step?.step_number}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm text-foreground">Day +{step?.days_after_due}</span>
                              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full capitalize">{step?.action_type}</span>
                              {!step?.is_active && <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">Inactive</span>}
                            </div>
                            <p className="text-sm text-muted-foreground truncate mt-0.5">{step?.subject}</p>
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => { setSelectedSchedule(schedule); setEditingStep(step); setStepForm({ step_number: step?.step_number, days_after_due: step?.days_after_due, action_type: step?.action_type, subject: step?.subject || '', message_template: step?.message_template || '', is_active: step?.is_active }); setShowStepModal(true); }} className="p-1.5 text-muted-foreground hover:text-foreground rounded">
                              <Icon name="Edit" size={14} />
                            </button>
                            <button onClick={() => handleDeleteStep(step?.id)} className="p-1.5 text-muted-foreground hover:text-red-500 rounded">
                              <Icon name="Trash2" size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground">{selectedSchedule?.id ? 'Edit Schedule' : 'New Dunning Schedule'}</h2>
              <button onClick={() => setShowScheduleModal(false)} className="text-muted-foreground hover:text-foreground"><Icon name="X" size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Schedule Name *</label>
                <input value={scheduleForm?.name} onChange={e => setScheduleForm(p => ({ ...p, name: e?.target?.value }))} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground" placeholder="e.g. Standard Dunning" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Description</label>
                <textarea value={scheduleForm?.description} onChange={e => setScheduleForm(p => ({ ...p, description: e?.target?.value }))} rows={3} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground" placeholder="Describe this schedule..." />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={scheduleForm?.is_default} onChange={e => setScheduleForm(p => ({ ...p, is_default: e?.target?.checked }))} className="rounded" />
                <span className="text-sm text-foreground">Set as default schedule</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 p-6 border-t border-border">
              <Button variant="outline" onClick={() => setShowScheduleModal(false)}>Cancel</Button>
              <Button onClick={handleSaveSchedule} disabled={!scheduleForm?.name}>Save Schedule</Button>
            </div>
          </div>
        </div>
      )}
      {/* Step Modal */}
      {showStepModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground">{editingStep?.id ? 'Edit Reminder Step' : 'Add Reminder Step'}</h2>
              <button onClick={() => { setShowStepModal(false); setEditingStep(null); }} className="text-muted-foreground hover:text-foreground"><Icon name="X" size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Step Number</label>
                  <input type="number" min={1} value={stepForm?.step_number} onChange={e => setStepForm(p => ({ ...p, step_number: parseInt(e?.target?.value) }))} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Days After Due</label>
                  <input type="number" min={0} value={stepForm?.days_after_due} onChange={e => setStepForm(p => ({ ...p, days_after_due: parseInt(e?.target?.value) }))} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Action Type</label>
                <select value={stepForm?.action_type} onChange={e => setStepForm(p => ({ ...p, action_type: e?.target?.value }))} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground">
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                  <option value="call">Phone Call</option>
                  <option value="suspend">Suspend Service</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Email Subject</label>
                <input value={stepForm?.subject} onChange={e => setStepForm(p => ({ ...p, subject: e?.target?.value }))} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground" placeholder="Use {{invoice_number}}, {{customer_name}}, {{amount}}" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Message Template</label>
                <textarea value={stepForm?.message_template} onChange={e => setStepForm(p => ({ ...p, message_template: e?.target?.value }))} rows={4} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground" placeholder="Use {{customer_name}}, {{invoice_number}}, {{amount}}, {{due_date}}" />
                <p className="text-xs text-muted-foreground mt-1">Variables: {'{{customer_name}}'}, {'{{invoice_number}}'}, {'{{amount}}'}, {'{{due_date}}'}</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={stepForm?.is_active} onChange={e => setStepForm(p => ({ ...p, is_active: e?.target?.checked }))} className="rounded" />
                <span className="text-sm text-foreground">Step is active</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 p-6 border-t border-border">
              <Button variant="outline" onClick={() => { setShowStepModal(false); setEditingStep(null); }}>Cancel</Button>
              <Button onClick={handleSaveStep} disabled={!stepForm?.subject}>Save Step</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentRemindersDunning;
